/**
 * Everything between "the player typed a room code" and "there is a runner to
 * fly": the relay socket, the WebRTC handshake, the lobby handshake, and the
 * phase machine that keeps them honest.
 *
 * This file is glue and is written to stay glue — every decision it makes is
 * either a table lookup in `session-state.ts` or a one-line dispatch on a
 * decoded message. The parts with real logic live either side of it
 * (`packages/net` for the protocol, `duel-runner.ts` for the simulation,
 * `duel-lobby.ts` for what the screen says), which is what keeps the piece
 * that can only be exercised against a real browser as small as possible.
 *
 * The relay socket is deliberately kept open after the peers connect: it is
 * the cheapest reliable "the other tab closed" signal there is, and losing the
 * relay itself mid-duel is *not* treated as fatal, because by then it is not
 * in the path.
 */

import { engineFingerprint, type AircraftKey, type FlightModelTier } from "@red-opus/engine";
import {
  DEFAULT_HASH_INTERVAL,
  DEFAULT_INPUT_DELAY,
  DuelMachine,
  NET_PROTOCOL_VERSION,
  browserPeer,
  browserSocket,
  buildMismatchNote,
  buildVerdict,
  createRtcSession,
  createSignalingClient,
  decodeMessage,
  encodeMessage,
  isFlying,
  type BuildIdentity,
  type BuildVerdict,
  type DuelMessage,
  type DuelSetup,
  type IceConfig,
  type NetTransport,
  type PeerFactory,
  type RtcSession,
  type SignalRole,
  type SignalingClient,
  type SocketFactory,
} from "@red-opus/net";
import { createDuelRunner, type DuelRunner } from "./duel-runner.ts";
import { DUEL_COMBAT_LEVEL } from "./duel-setup.ts";
import type { DuelStatus } from "./duel-lobby.ts";
import type { DuelOutcome } from "./duel-outcome.ts";

export interface DuelConnectionOptions {
  readonly room: string;
  readonly signalUrl: string;
  readonly ice: IceConfig;
  readonly aircraft: AircraftKey;
  /** Called after any state change, so a Solid signal can be bumped. */
  readonly onChange: () => void;
  /** 32-bit duel seed. Injected so a test can pin one. */
  readonly newSeed: () => number;
  readonly socketFactory?: SocketFactory;
  readonly peerFactory?: PeerFactory;
  /**
   * This build's identity for the gate below. Injected so a test can play a
   * peer from another build without building one; production passes nothing
   * and gets `NET_PROTOCOL_VERSION` + `engineFingerprint()`.
   */
  readonly identity?: BuildIdentity;
}

/** What this build is, unless a caller says otherwise. Memoized inside `engineFingerprint`. */
function localIdentity(options: DuelConnectionOptions): BuildIdentity {
  return options.identity ?? { protocol: NET_PROTOCOL_VERSION, engine: engineFingerprint() };
}

export interface DuelConnection {
  status(): DuelStatus;
  runner(): DuelRunner | null;
  /** Changes this player's aeroplane and tells the other side. Lobby only. */
  choose(aircraft: AircraftKey): void;
  aircraft(): AircraftKey;
  /** Host only: agrees the duel and starts it on both machines. */
  launch(tier: FlightModelTier): void;
  /** Host only: same room, same aeroplanes, new seed. */
  rematch(tier: FlightModelTier): void;
  leave(): void;
}

class Session implements DuelConnection {
  private readonly machine = new DuelMachine();
  private readonly signaling: SignalingClient;
  private role: SignalRole | null = null;
  private transport: NetTransport | null = null;
  private rtc: RtcSession | null = null;
  private duel: DuelRunner | null = null;
  private chosen: AircraftKey;
  private opponent: AircraftKey | null = null;
  private lastOutcome: DuelOutcome = "flying";
  private error: string | null = null;
  private readonly mine: BuildIdentity;
  private theirs: BuildIdentity | null = null;

  constructor(private readonly options: DuelConnectionOptions) {
    this.mine = localIdentity(options);
    this.chosen = options.aircraft;
    this.signaling = createSignalingClient(
      options.signalUrl,
      {
        onRole: (role) => {
          this.role = role;
          this.options.onChange();
        },
        onPeer: (present) => (present ? this.peerArrived() : this.fail("Your opponent left the room.")),
        onSignal: (signal) => void this.rtc?.accept(signal),
        // Once the DataChannel is up the relay is out of the path, so losing
        // it then is not a reason to end a duel that is flying perfectly well.
        onClosed: (reason) => {
          if (this.transport === null) this.fail(reason);
        },
      },
      options.socketFactory ?? browserSocket,
    );
    this.machine.apply("join");
  }

  private send(message: DuelMessage): void {
    this.transport?.send(encodeMessage(message));
  }

  private fail(reason: string): void {
    this.error ??= reason;
    this.machine.apply("peerLeft");
    this.duel?.finish("disconnected");
    this.lastOutcome = this.duel ? this.duel.outcome() : "disconnected";
    this.options.onChange();
  }

  private peerArrived(): void {
    this.machine.apply("peerJoined");
    if (this.rtc || this.role === null) return;
    const peer = (this.options.peerFactory ?? browserPeer)(this.options.ice);
    this.rtc = createRtcSession(this.role, peer, {
      onSignal: (signal) => this.signaling.send(signal),
      onOpen: (transport) => this.channelOpen(transport),
      onClose: (reason) => this.fail(reason),
    });
    void this.rtc.start();
    this.options.onChange();
  }

  private channelOpen(transport: NetTransport): void {
    this.transport = transport;
    transport.receive((frame) => {
      const message = decodeMessage(frame);
      // A frame this build cannot read, on a channel only this build writes
      // to, is a peer that is not this build — the shape an older protocol
      // version arrives in, since it has no `hello` of its own to send. Saying
      // so beats waiting for a `pick` that will never parse.
      if (message) this.receive(message);
      else this.refuseBuild(null);
    });
    this.machine.apply("channelOpen");
    // `hello` first, so the verdict is in before either player can press
    // anything, and `pick` in the same burst so a matching peer loses nothing.
    this.send({ t: "hello", v: this.mine.protocol, engine: this.mine.engine });
    this.send({ t: "pick", v: NET_PROTOCOL_VERSION, aircraft: this.chosen });
    this.options.onChange();
  }

  /** The one place a build difference ends a duel, whichever of the three ways it was proved. */
  private refuseBuild(theirs: BuildIdentity | null): void {
    if (this.machine.phase === "mismatched") return;
    this.theirs = theirs;
    this.error ??= buildMismatchNote(this.mine, theirs);
    this.duel?.finish("disconnected");
    this.duel = null;
    this.machine.apply("buildMismatch");
    this.options.onChange();
  }

  private buildVerdict(): BuildVerdict {
    return buildVerdict(this.mine, this.theirs);
  }

  private receive(message: DuelMessage): void {
    if (message.t === "hello") {
      const theirs: BuildIdentity = { protocol: message.v, engine: message.engine };
      if (buildVerdict(this.mine, theirs) === "different") return this.refuseBuild(theirs);
      this.theirs = theirs;
      this.options.onChange();
      return;
    }
    if (message.t === "pick") {
      this.opponent = message.aircraft;
      this.options.onChange();
      return;
    }
    if (message.t === "setup" && this.role === "guest") {
      // The guest's own gate, and the authoritative one: `hello` can be lost,
      // a host could skip it, and this is the frame the fight is actually
      // built from — so the fingerprint it carries is checked against ours
      // before a single tick is stepped.
      if (message.setup.engine !== this.mine.engine) {
        return this.refuseBuild({ protocol: message.v, engine: message.setup.engine });
      }
      this.begin(message.setup, "guest");
      this.send({ t: "ready", v: NET_PROTOCOL_VERSION });
      return;
    }
    if (message.t === "ready") return;
    this.duel?.accept(message);
    if (message.t === "end" && message.reason !== "decided") this.options.onChange();
  }

  private begin(setup: DuelSetup, role: SignalRole): void {
    this.duel = createDuelRunner(setup, role, {
      onSend: (message) => this.send(message),
      onDesync: () => {
        this.machine.apply("hashMismatch");
        this.send({ t: "end", v: NET_PROTOCOL_VERSION, reason: "desync" });
        this.options.onChange();
      },
      onDecided: (outcome) => {
        this.lastOutcome = outcome;
        if (isFlying(this.machine.phase)) this.machine.apply(outcome === "desync" ? "hashMismatch" : "duelOver");
        this.options.onChange();
      },
    });
    this.lastOutcome = "flying";
    if (this.machine.phase === "ended") this.machine.apply("rematch");
    this.machine.apply("setupAgreed");
    this.options.onChange();
  }

  /**
   * The host's launch, and the whole negotiation: every quantity the
   * simulation will read is decided here, once, and travels in one frame.
   * Nothing downstream may re-derive any of it locally — see `DuelSetup`.
   */
  private agree(tier: FlightModelTier): void {
    if (this.role !== "host" || this.opponent === null) return;
    // Never launch on an unconfirmed peer: a duel agreed before `hello` landed
    // is a duel whose desync would arrive instead of a message.
    if (this.buildVerdict() !== "same") return;
    const setup: DuelSetup = {
      seed: this.options.newSeed() >>> 0,
      tier,
      host: this.chosen,
      guest: this.opponent,
      inputDelay: DEFAULT_INPUT_DELAY,
      hashInterval: DEFAULT_HASH_INTERVAL,
      combatLevel: DUEL_COMBAT_LEVEL,
      engine: this.mine.engine,
    };
    this.send({ t: "setup", v: NET_PROTOCOL_VERSION, setup });
    this.begin(setup, "host");
  }

  status(): DuelStatus {
    return {
      phase: this.machine.phase,
      role: this.role,
      room: this.options.room,
      outcome: this.duel?.outcome() ?? this.lastOutcome,
      opponentAircraft: this.opponent,
      waiting: this.duel?.waiting() ?? false,
      build: this.buildVerdict(),
      error: this.error,
    };
  }

  runner(): DuelRunner | null {
    return this.duel;
  }

  aircraft(): AircraftKey {
    return this.chosen;
  }

  choose(aircraft: AircraftKey): void {
    this.chosen = aircraft;
    this.send({ t: "pick", v: NET_PROTOCOL_VERSION, aircraft });
    this.options.onChange();
  }

  launch(tier: FlightModelTier): void {
    this.agree(tier);
  }

  rematch(tier: FlightModelTier): void {
    if (this.machine.phase !== "ended") return;
    this.duel = null;
    this.machine.apply("rematch");
    this.agree(tier);
  }

  leave(): void {
    this.send({ t: "end", v: NET_PROTOCOL_VERSION, reason: "quit" });
    this.duel?.finish("disconnected");
    this.rtc?.close();
    this.signaling.close();
    this.transport = null;
    this.options.onChange();
  }
}

export function createDuelConnection(options: DuelConnectionOptions): DuelConnection {
  return new Session(options);
}
